/**
 * Genera un PDF real (Blob) a partir del mismo HTML que la vista/imprimir de factura.
 * Usado para descarga y para Web Share API (adjuntar PDF en WhatsApp en móvil).
 *
 * Nota: imágenes remotas (logo) pueden hacer fallar html2canvas por CORS; se intenta
 * incrustar el logo como data URL; si falla, se reintenta sin logo.
 */
import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';
import type { Factura } from '../components/facturas/types';
import { buildFacturaPdfHtml, type FacturaPdfCompanyInfo } from './facturaPdfHtml';

function canvasToPdfBlob(canvas: HTMLCanvasElement): Blob {
  const imgData = canvas.toDataURL('image/png', 1.0);
  const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
  const pageW = pdf.internal.pageSize.getWidth();
  const pageH = pdf.internal.pageSize.getHeight();
  const imgW = pageW;
  const imgH = (canvas.height * pageW) / canvas.width;

  let heightLeft = imgH;
  let position = 0;
  pdf.addImage(imgData, 'PNG', 0, position, imgW, imgH);
  heightLeft -= pageH;
  while (heightLeft > 1) {
    position -= pageH;
    pdf.addPage();
    pdf.addImage(imgData, 'PNG', 0, position, imgW, imgH);
    heightLeft -= pageH;
  }
  return pdf.output('blob');
}

async function blobToDataUrl(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onloadend = () => resolve(String(r.result));
    r.onerror = () => reject(new Error('FileReader'));
    r.readAsDataURL(blob);
  });
}

/** Intenta convertir URL de imagen a data URL para que html2canvas no falle por CORS. */
export async function tryInlineImageUrlAsDataUrl(url: string | null | undefined): Promise<string | null> {
  const u = String(url || '').trim();
  if (!u || u.startsWith('data:')) return u || null;
  try {
    const res = await fetch(u, { mode: 'cors', credentials: 'omit', cache: 'force-cache' });
    if (!res.ok) return null;
    const blob = await res.blob();
    if (!blob.type.startsWith('image/')) return null;
    return await blobToDataUrl(blob);
  } catch {
    return null;
  }
}

async function inlineCompanyLogoForPdf(info: FacturaPdfCompanyInfo): Promise<FacturaPdfCompanyInfo> {
  const dataUrl = await tryInlineImageUrlAsDataUrl(info.companyLogo);
  if (dataUrl) return { ...info, companyLogo: dataUrl };
  return { ...info, companyLogo: null };
}

/** Cualquier documento HTML completo → PDF (misma tubería que la factura). */
export async function renderHtmlDocumentToPdfBlob(html: string): Promise<Blob> {
  const iframe = document.createElement('iframe');
  iframe.setAttribute('aria-hidden', 'true');
  Object.assign(iframe.style, {
    position: 'fixed',
    left: '-12000px',
    top: '0',
    width: '794px',
    height: '1200px',
    border: 'none',
    opacity: '0',
    pointerEvents: 'none',
  });
  document.body.appendChild(iframe);

  try {
    const doc = iframe.contentDocument;
    if (!doc) throw new Error('iframe document');
    doc.open();
    doc.write(html);
    doc.close();

    await new Promise<void>((resolve) => {
      const w = iframe.contentWindow;
      if (!w) {
        resolve();
        return;
      }
      if (w.document.readyState === 'complete') resolve();
      else iframe.onload = () => resolve();
    });

    await new Promise((r) => setTimeout(r, 500));

    const body = doc.body;
    const canvas = await html2canvas(body, {
      scale: 2,
      useCORS: true,
      logging: false,
      backgroundColor: '#ffffff',
      windowWidth: body.scrollWidth,
      windowHeight: body.scrollHeight,
      imageTimeout: 20000,
    });

    return canvasToPdfBlob(canvas);
  } finally {
    iframe.remove();
  }
}

export async function buildFacturaPdfBlob(factura: Factura, info: FacturaPdfCompanyInfo): Promise<Blob> {
  const infoInlined = await inlineCompanyLogoForPdf(info);
  try {
    return await renderHtmlDocumentToPdfBlob(buildFacturaPdfHtml(factura, infoInlined));
  } catch (e) {
    console.warn('[facturaPdfBlob] retry PDF without logo', e);
    const noLogo: FacturaPdfCompanyInfo = { ...infoInlined, companyLogo: null };
    return await renderHtmlDocumentToPdfBlob(buildFacturaPdfHtml(factura, noLogo));
  }
}

/** Descarga el PDF; en Safari conviene anclar el <a> al DOM y demorar revoke. */
export function downloadFacturaPdfBlob(blob: Blob, filename: string): void {
  const name = filename.toLowerCase().endsWith('.pdf') ? filename : `${filename}.pdf`;
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  a.rel = 'noopener';
  a.style.display = 'none';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  window.setTimeout(() => URL.revokeObjectURL(url), 2500);
}

/** Abre el PDF en una pestaña nueva (respaldo si la descarga no guarda archivo). */
export function openFacturaPdfBlobInNewTab(blob: Blob): void {
  const url = URL.createObjectURL(blob);
  const w = window.open(url, '_blank', 'noopener,noreferrer');
  if (!w) {
    URL.revokeObjectURL(url);
    return;
  }
  window.setTimeout(() => URL.revokeObjectURL(url), 120000);
}

export function safeFacturaPdfFilename(numeroMostrar: string): string {
  const safe = String(numeroMostrar || 'factura').replace(/[^\w-]+/g, '_');
  return `Factura_${safe}.pdf`;
}

/** Safari (macOS/iOS), Chrome Android, etc.: hoja «Compartir» con archivo PDF. */
export function canSharePdfFiles(): boolean {
  if (typeof window === 'undefined') return false;
  const nav = navigator as Navigator & {
    share?: (data: ShareData & { files?: File[] }) => Promise<void>;
    canShare?: (data: ShareData & { files?: File[] }) => boolean;
  };
  if (typeof nav.share !== 'function' || typeof nav.canShare !== 'function') return false;
  try {
    const probe = new Uint8Array([0x25, 0x50, 0x44, 0x46]); // "%PDF"
    const f = new File([probe], 'probe.pdf', { type: 'application/pdf' });
    return nav.canShare({ files: [f] });
  } catch {
    return false;
  }
}

/** Intenta compartir el PDF (p. ej. WhatsApp nativo en Android). Devuelve true si se usó share. */
export async function shareFacturaPdfBlob(
  blob: Blob,
  filename: string,
  opts: { text?: string; title?: string },
): Promise<boolean> {
  const name = filename.toLowerCase().endsWith('.pdf') ? filename : `${filename}.pdf`;
  const file = new File([blob], name, { type: 'application/pdf' });
  const nav = navigator as Navigator & {
    share?: (data: ShareData & { files?: File[] }) => Promise<void>;
    canShare?: (data: { files: File[] }) => boolean;
  };
  if (!nav.share || !nav.canShare?.({ files: [file] })) {
    return false;
  }
  try {
    await nav.share({
      files: [file],
      text: opts.text,
      title: opts.title,
    });
    return true;
  } catch (e: unknown) {
    if (e && typeof e === 'object' && (e as { name?: string }).name === 'AbortError') {
      throw e;
    }
    return false;
  }
}
